import type { ControlBridgeQueueItem } from './deriveViewModel';
import type { KryspaceControlBridgeStructure, ControlBridgeStructureNode } from '../../types/kryspaceControlBridgeStructure';
import type { ScopeContext } from './filters';
import { scopeKeys } from './filters';
import { queueItemMatchesStructureScopeId, scopeIdsEqual } from '../../utils/scopeIdPlane';

export interface SubdomainFlowRow {
  scopeId: string;
  label: string;
  role?: string;
  activeActivities: number;
  openIssues: number;
  prereqBlockers: number;
  resourceBlockers: number;
  activities: ControlBridgeQueueItem[];
  issues: ControlBridgeQueueItem[];
}

const PREREQ_PATTERN = /blocked|prereq|waiting|dependency/i;
const RESOURCE_PATTERN = /resource|capacity|unassigned|constraint/i;

export function isPrereqBlocker(item: ControlBridgeQueueItem): boolean {
  if (PREREQ_PATTERN.test(item.statusPhrase)) return true;
  if (item.issueType && /PREREQ|BLOCK|DEPEND/i.test(item.issueType)) return true;
  if (item.activityTargetState && /BLOCK/i.test(item.activityTargetState)) return true;
  return false;
}

export function isResourceBlocker(item: ControlBridgeQueueItem): boolean {
  if (RESOURCE_PATTERN.test(item.statusPhrase)) return true;
  if (item.issueType && /RESOURCE|CAPACITY/i.test(item.issueType)) return true;
  if (!item.assignedResourceLabel && !item.assignedResourceRef && /active|ready|eligible/i.test(item.statusPhrase)) {
    return true;
  }
  return false;
}

/**
 * Improvement-meta twins (disk `co-*-improvement-meta-*`), not operational item-plane subdomains.
 */
export function isImprovementMetaTwinNode(node: ControlBridgeStructureNode): boolean {
  const id = (node.filterScope || node.key || '').trim().toLowerCase();
  const label = (node.label || '').trim().toLowerCase();
  return id.includes('improvement-meta') || label.includes('improvement-meta');
}

/**
 * Drop improvement-meta twins from Drive/Diagnose/sidebar rails.
 * Keep operational item-plane ``ai-*`` subdomains and Fred ``ca-*`` / UUID lanes.
 */
export function isDriveScaffoldingSubdomainNode(node: ControlBridgeStructureNode): boolean {
  return isImprovementMetaTwinNode(node);
}

function descendantScopeIds(node: ControlBridgeStructureNode): string[] {
  const out: string[] = [];
  for (const child of node.children ?? []) {
    const id = child.filterScope ?? child.key;
    if (id) out.push(id);
    out.push(...descendantScopeIds(child));
  }
  return out;
}

function scopeIdsForNode(node: ControlBridgeStructureNode, includeChildren: boolean): string[] {
  const self = node.filterScope ?? node.key;
  if (!includeChildren) {
    return [self];
  }
  return [self, ...descendantScopeIds(node)];
}

function itemMatchesScopes(item: ControlBridgeQueueItem, scopes: string[]): boolean {
  return scopes.some((scope) => queueItemMatchesStructureScopeId(item, scope));
}

function partitionForScopes(
  openIssues: ControlBridgeQueueItem[],
  eligibleWork: ControlBridgeQueueItem[],
  scopes: string[]
): Pick<SubdomainFlowRow, 'activities' | 'issues' | 'activeActivities' | 'openIssues' | 'prereqBlockers' | 'resourceBlockers'> {
  const activities = eligibleWork.filter((item) => itemMatchesScopes(item, scopes));
  const issues = openIssues.filter((item) => itemMatchesScopes(item, scopes));
  const combined = [...activities, ...issues];
  return {
    activities,
    issues,
    activeActivities: activities.length,
    openIssues: issues.length,
    prereqBlockers: combined.filter(isPrereqBlocker).length,
    resourceBlockers: combined.filter(isResourceBlocker).length,
  };
}

function nodeHasQueueOccupancy(
  node: ControlBridgeStructureNode,
  openIssues: ControlBridgeQueueItem[],
  eligibleWork: ControlBridgeQueueItem[]
): boolean {
  const partition = partitionForScopes(openIssues, eligibleWork, scopeIdsForNode(node, true));
  return partition.activeActivities + partition.openIssues > 0;
}

/**
 * Prefer CA / operational item-plane lanes; drop improvement-meta twins.
 */
export function filterDriveLaneChildNodes(
  children: ControlBridgeStructureNode[],
  openIssues: ControlBridgeQueueItem[],
  eligibleWork: ControlBridgeQueueItem[]
): ControlBridgeStructureNode[] {
  if (!children.length) return [];
  const real = children.filter((child) => !isDriveScaffoldingSubdomainNode(child));
  if (real.length > 0) return real;
  return children.filter((child) => nodeHasQueueOccupancy(child, openIssues, eligibleWork));
}

function rowFromNode(node: ControlBridgeStructureNode, includeChildren: boolean, openIssues: ControlBridgeQueueItem[], eligibleWork: ControlBridgeQueueItem[]): SubdomainFlowRow {
  const scopes = scopeIdsForNode(node, includeChildren);
  const partition = partitionForScopes(openIssues, eligibleWork, scopes);
  return {
    scopeId: node.filterScope ?? node.key,
    label: node.label,
    role: node.role,
    ...partition,
  };
}

function findNode(structure: KryspaceControlBridgeStructure, scope: string): ControlBridgeStructureNode | undefined {
  const walk = (node: ControlBridgeStructureNode): ControlBridgeStructureNode | undefined => {
    if (scopeIdsEqual(node.filterScope ?? node.key, scope)) return node;
    for (const child of node.children ?? []) {
      const hit = walk(child);
      if (hit) return hit;
    }
    return undefined;
  };
  for (const group of structure.groups) {
    for (const node of group.nodes) {
      const hit = walk(node);
      if (hit) return hit;
    }
  }
  return undefined;
}

function isDomainNode(node: ControlBridgeStructureNode): boolean {
  return node.role === 'domain' || (node.children?.length ?? 0) > 0;
}

/**
 * Drive surface rows: company → top-level domains; domain → real subdomains (not empty scaffolding).
 */
export function deriveDriveSubdomainRows(
  structure: KryspaceControlBridgeStructure,
  scope: string,
  openIssues: ControlBridgeQueueItem[],
  eligibleWork: ControlBridgeQueueItem[],
  _scopeContext?: ScopeContext
): SubdomainFlowRow[] {
  const companyWide = !scope || scope === 'company';
  const selectedNode = companyWide ? undefined : findNode(structure, scope);

  if (selectedNode) {
    const kids = filterDriveLaneChildNodes(selectedNode.children ?? [], openIssues, eligibleWork);
    if (kids.length > 0) {
      return kids.map((child) => rowFromNode(child, true, openIssues, eligibleWork));
    }
    return [rowFromNode(selectedNode, true, openIssues, eligibleWork)];
  }

  const rows: SubdomainFlowRow[] = [];
  for (const group of structure.groups) {
    if (group.id !== 'domains' && group.id !== 'domain_bridges') continue;
    for (const node of group.nodes) {
      if (!isDomainNode(node)) continue;
      if (companyWide) {
        rows.push(rowFromNode(node, true, openIssues, eligibleWork));
      }
    }
  }

  if (rows.length === 0 && companyWide) {
    for (const group of structure.groups) {
      for (const node of group.nodes) {
        if (node.role === 'subdomain') continue;
        if (isDriveScaffoldingSubdomainNode(node)) continue;
        rows.push(rowFromNode(node, true, openIssues, eligibleWork));
      }
    }
  }

  return rows;
}

/** Scope keys used when filtering queue items for a sidebar selection (Diagnose reuse). */
export function activeScopeKeys(scope: string, ctx?: ScopeContext): string[] {
  if (!scope || scope === 'company') return [];
  return scopeKeys(scope, ctx);
}
